import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  MenuItem,
  IconButton,
  Link,
  useTheme,
  useMediaQuery,
  Chip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  WhatsApp,
  Instagram,
  Facebook,
  YouTube,
  Send,
  CheckCircle
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import bgImg from '../../../assets/images/windows_os_banner.png';
import SEO from '../../common/SEO';

const contactInfo = [
  {
    icon: <Phone />,
    label: 'CALL US',
    value: '+91 91132 99131 and +91 99019 91771',
    color: '#0e6ada',
    href: 'tel:+919113299131'
  },
  {
    icon: <WhatsApp />,
    label: 'WHATSAPP',
    value: 'Chat with us',
    color: '#25D366',
    href: 'https://wa.me/919113299131'
  },
  {
    icon: <Email />,
    label: 'EMAIL US',
    value: 'arinfotechsolution11@gmail.com',
    color: '#0e6ada',
    href: 'mailto:arinfotechsolution11@gmail.com'
  },
  {
    icon: <LocationOn />,
    label: 'VISIT US',
    value: 'Unit 101, Oxford Towers, 139/88, Hal Old Airport Rd Kodihalli, Bangalore, Karnataka, India, 560008',
    color: '#e53935',
    href: 'https://maps.google.com/?q=JP+Nagar+7th+Phase+Bengaluru'
  }
];

const ContactPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [snackOpen, setSnackOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: 'order', message: '' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    setSnackOpen(true);
    setFormData({ name: '', email: '', phone: '', subject: 'order', message: '' });
  };

  return (
    <Box>
      <SEO
        title="Contact AR Infotech | Software License Support"
        description="Get in touch with AR Infotech for genuine Microsoft license support, bulk orders, or activation help. Call, WhatsApp, or email us."
      />

      {/* ─── HERO BANNER with blurred bg ─── */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 280, md: 380 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Blurred background image */}
        <Box
          component="img"
          src={bgImg}
          alt=""
          sx={{
            position: 'absolute',
            top: 0, left: 0, width: '100%', height: '100%',
            objectFit: 'cover',
            filter: 'blur(6px) brightness(0.45)',
            transform: 'scale(1.08)',
          }}
        />
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            background: 'linear-gradient(135deg, rgba(10,22,60,0.75) 0%, rgba(14,100,200,0.55) 100%)',
          }}
        />

        {/* Center content */}
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center', py: { xs: 6, md: 8 } }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Chip
              label="💬 We're Here to Help"
              sx={{
                mb: 3, backgroundColor: 'rgba(0,210,196,0.15)',
                border: '1px solid rgba(0,210,196,0.5)',
                color: '#00D2C4', fontWeight: 700, fontSize: '0.85rem', px: 1
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#FFF', fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 2, textShadow: '0 2px 20px rgba(0,0,0,0.4)'
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.85)', fontWeight: 400,
                maxWidth: 600, mx: 'auto', lineHeight: 1.7
              }}
            >
              Have a question about our software licenses? We're ready to help you get the right product activated.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* ─── MAIN CONTENT (Form + Info) floating on bg ─── */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(160deg, #EBF6FF 0%, #F5FAFF 60%, #FFFFFF 100%)',
          pt: { xs: 5, md: 8 },
          pb: { xs: 6, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={isMobile ? 4 : 6} justifyContent="center">

            {/* ── Contact Form ── */}
            <Grid item xs={12} md={7}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, sm: 5 },
                    borderRadius: 3,
                    border: '1px solid rgba(14,106,218,0.12)',
                    boxShadow: '0 20px 60px rgba(14,106,218,0.08)',
                    background: '#FFFFFF',
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: '#0e3a7a' }}>
                    Send Us a Message
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Fill in the form and our team will get back to you within 1 business hour.
                  </Typography>

                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="Your Name" name="name"
                          value={formData.name} onChange={handleChange}
                          variant="outlined" required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="Email Address" name="email" type="email"
                          value={formData.email} onChange={handleChange}
                          variant="outlined" required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth label="Phone Number" name="phone"
                          value={formData.phone} onChange={handleChange}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth select label="Subject" name="subject"
                          value={formData.subject} onChange={handleChange}
                          variant="outlined"
                        >
                          <MenuItem value="order">Order Query</MenuItem>
                          <MenuItem value="product">License Activation Help</MenuItem>
                          <MenuItem value="wholesale">Bulk Licensing</MenuItem>
                          <MenuItem value="feedback">Feedback</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth multiline rows={4} label="Your Message" name="message"
                          value={formData.message} onChange={handleChange}
                          variant="outlined" required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          endIcon={<Send />}
                          sx={{
                            px: 6, py: 1.5, fontWeight: 700,
                            borderRadius: 2, fontSize: '1rem',
                            background: 'linear-gradient(135deg, #0e6ada 0%, #0099cc 100%)',
                            boxShadow: '0 6px 20px rgba(14,106,218,0.35)',
                            '&:hover': { boxShadow: '0 8px 28px rgba(14,106,218,0.5)' }
                          }}
                        >
                          Send Message
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </motion.div>
            </Grid>

            {/* ── Contact Info ── */}
            <Grid item xs={12} md={5}>
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, height: '100%' }}>
                  {contactInfo.map((info, i) => (
                    <Paper
                      key={i}
                      component={Link}
                      href={info.href}
                      target={info.href.startsWith('http') ? '_blank' : undefined}
                      underline="none"
                      elevation={0}
                      sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 2.5,
                        p: { xs: 2.5, sm: 3 }, borderRadius: 3,
                        border: '1px solid rgba(14,106,218,0.1)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                        background: '#FFFFFF',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        textDecoration: 'none',
                        '&:hover': {
                          boxShadow: '0 8px 30px rgba(14,106,218,0.15)',
                          transform: 'translateY(-3px)',
                          borderColor: 'rgba(14,106,218,0.3)',
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 48, height: 48, borderRadius: 2, flexShrink: 0,
                          backgroundColor: `${info.color}18`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: info.color,
                        }}
                      >
                        {info.icon}
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}>
                          {info.label}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a2e', mt: 0.3, lineHeight: 1.5 }}>
                          {info.value}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}

                  {/* Social links */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3, borderRadius: 3,
                      border: '1px solid rgba(14,106,218,0.1)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                      background: '#FFFFFF',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: '#0e3a7a' }}>
                      Follow Us
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      {[
                        { icon: <Instagram />, color: '#E1306C', href: '#' },
                        { icon: <Facebook />, color: '#1877F2', href: '#' },
                        { icon: <YouTube />, color: '#FF0000', href: '#' },
                        { icon: <WhatsApp />, color: '#25D366', href: 'https://wa.me/919113299131' },
                      ].map((s, i) => (
                        <IconButton
                          key={i} href={s.href} target="_blank"
                          sx={{
                            width: 44, height: 44,
                            border: `2px solid ${s.color}30`,
                            color: s.color,
                            '&:hover': { backgroundColor: `${s.color}15`, borderColor: s.color }
                          }}
                        >
                          {s.icon}
                        </IconButton>
                      ))}
                    </Box>
                  </Paper>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── Google Map ─── */}
      <Box sx={{ height: { xs: 300, md: 420 }, width: '100%' }}>
        <iframe
          title="AR Infotech Location"
          src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3889.171164455281!2d77.57992207507517!3d12.896712987411837!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMTLCsDUzJzQ4LjIiTiA3N8KwMzQnNTcuMCJF!5e0!3m2!1sen!2sin!4v1777893738927!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0, display: 'block' }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </Box>

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackOpen(false)} severity="success" variant="filled" icon={<CheckCircle />}>
          Message sent! We'll get back to you shortly.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;
